/**
 * BeatSettingsPanel - Beat type and frequency controls
 * Displays current brainwave state
 */
import React, { memo } from 'react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { useTheme } from '../contexts/ThemeContext';
import BreathPacer from './BreathPacer';

const BEAT_TYPES = {
  binaural: { name: 'Binaural Beats', emoji: '🎧' },
  monaural: { name: 'Monaural Beats', emoji: '🔊' },
  isochronic: { name: 'Isochronic Tones', emoji: '⚡' },
  trypnaural: { name: 'Trypnaural Beats', emoji: '🌀' },
  heartbeat: { name: 'Heartbeat Rhythm', emoji: '💓' },
};

// Map a coherence percentage to a friendly word the user understands at a glance.
const coherenceLabel = (pct) => {
  if (pct <= 0) return 'Strict (metronome)';
  if (pct <= 2) return 'Tight';
  if (pct <= 5) return 'Organic';
  if (pct <= 8) return 'Relaxed';
  return 'Deeply relaxed';
};

const BeatSettingsPanel = memo(({
  beatType,
  baseFrequency,
  brainwaveState,
  onBeatTypeChange,
  onBaseFrequencyChange,
  // Coherence (HRV) — only visible when heartbeat is selected.
  coherence = 4,
  onCoherenceChange,
  // Breath pacer — visual breathing guide. Pairs with heartbeat / coherence.
  breathPacerEnabled = false,
  onBreathPacerToggle,
  breathPacerBpm = 5.5,
  onBreathPacerBpmChange,
  breathPacerIntroPulse = false,
  isPlaying = false
}) => {
  const { theme } = useTheme();
  // Defensive fallback: older cached custom themes (pre-Feb 2026) won't have
  // a primaryText token, so default to text-white to keep parity with the
  // existing dark-only design while making future light-theme support trivial.
  const primaryText = theme.primaryText || 'text-white';

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className={`${primaryText} text-sm`}>Beat Type</label>
        <Select value={beatType} onValueChange={onBeatTypeChange}>
          <SelectTrigger className={theme.inputBg}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={theme.selectBg}>
            {Object.entries(BEAT_TYPES).map(([key, { name, emoji }]) => (
              <SelectItem key={key} value={key} className={primaryText}>
                {emoji} {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label className={`${primaryText} text-sm`}>Base Frequency (Hz)</label>
        <Input
          type="number"
          value={baseFrequency}
          onChange={(e) => onBaseFrequencyChange(parseFloat(e.target.value) || 222)}
          onFocus={(e) => e.target.select()}
          min={20}
          max={1000}
          className={theme.inputBg}
        />
        <div className={`${theme.dimText} text-xs`}>
          Common: 222 Hz (clarity), 432 Hz (harmony), 528 Hz (healing)
        </div>
      </div>

      {beatType === 'heartbeat' && onCoherenceChange && (
        <div className="space-y-2" data-testid="coherence-control">
          <div className="flex items-center justify-between">
            <label className={`${primaryText} text-sm`}>Coherence</label>
            <span className={`${theme.mutedText} text-xs`}>
              {coherence === 0 ? '0%' : `±${coherence}%`} · {coherenceLabel(coherence)}
            </span>
          </div>
          <Slider
            value={[coherence]}
            onValueChange={(v) => onCoherenceChange(Array.isArray(v) ? v[0] : v)}
            min={0}
            max={10}
            step={1}
            data-testid="coherence-slider"
            className="my-1"
          />
          <div className={`${theme.dimText} text-xs`}>
            Heart-rate variability. 0% = strict metronome (precise entrainment).
            4% = organic (default). 8–10% mimics a parasympathetic-dominant resting state.
          </div>
        </div>
      )}

      {beatType === 'heartbeat' && onBreathPacerToggle && (
        <BreathPacer
          enabled={breathPacerEnabled}
          onToggle={onBreathPacerToggle}
          bpm={breathPacerBpm}
          onBpmChange={onBreathPacerBpmChange}
          introPulse={breathPacerIntroPulse}
        />
      )}

      <div 
        className={`${theme.infoBg} border p-4 rounded-lg`}
        style={{ 
          borderLeft: `4px solid ${brainwaveState?.color || '#10B981'}`,
          transition: 'border-left-color 700ms ease-out',
        }}
        data-testid="current-brainwave-card"
      >
        <div className={`${theme.mutedText} text-xs mb-1`}>Current Brainwave State</div>
        {/* Keyed by band name so each transition (Beta → Alpha → Theta → Delta)
            triggers a fresh fade-in. Pure visual confirmation that the
            timeline-driven frequency automation is alive. */}
        <div
          key={brainwaveState?.name || 'alpha'}
          className="animate-in fade-in duration-700"
        >
          <div
            className="text-2xl font-bold"
            style={{
              color: brainwaveState?.color || '#10B981',
              transition: 'color 700ms ease-out',
            }}
            data-testid="current-brainwave-name"
          >
            {brainwaveState?.name || 'Alpha'}
          </div>
          <div className={`${primaryText} text-sm`} data-testid="current-brainwave-frequency">
            {brainwaveState?.frequency?.toFixed(1) || '10.0'} Hz
          </div>
          <div className={`${theme.mutedText} text-xs mt-1`}>
            {brainwaveState?.description || 'Relaxed awareness, light meditation'}
          </div>
        </div>
      </div>

      {isPlaying && (
        <div className="flex items-center gap-2 text-green-400 text-sm">
          <span className="animate-pulse">●</span>
          Playing {BEAT_TYPES[beatType]?.name || 'beats'}
        </div>
      )}
    </div>
  );
});

BeatSettingsPanel.displayName = 'BeatSettingsPanel';

export default BeatSettingsPanel;
